import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Star, Send, ChevronLeft, ChevronRight, CheckCircle, Clock } from "lucide-react";
import { useCustomerAuth } from "../../../../context/CustomerAuthContext/CustomerAuthContext";
import axiosInstance from "../../../../api/axiosInstance";

/* ─── Types ─── */
type YorumOzet = {
  toplamAdet: number; ortalama: number;
  puan5: number; puan4: number; puan3: number; puan2: number; puan1: number;
};
type Yorum = {
  yorumId: number; musteriId: number; musteriAdSoyad: string;
  puan: number; yorumMetni?: string; olusturmaTarihi: string;
};
type MusteriDurum = {
  yorumYapildi: boolean; satinAldi: boolean; yorumId: number; puan: number;
  yorumMetni?: string; onayliMi: boolean;
};

/* ─── Star component ─── */
const Stars: React.FC<{
  value: number;
  max?: number;
  size?: number;
  interactive?: boolean;
  onChange?: (v: number) => void;
}> = ({ value, max = 5, size = 16, interactive = false, onChange }) => {
  const [hover, setHover] = useState(0);
  const display = interactive ? (hover || value) : value;

  return (
    <div className="inline-flex items-center gap-0.5">
      {Array.from({ length: max }, (_, i) => i + 1).map((s) => (
        <Star
          key={s}
          size={size}
          className={[
            "transition-colors",
            display >= s ? "fill-amber-400 text-amber-400" : "text-slate-200 fill-slate-200",
            interactive ? "cursor-pointer hover:scale-110 transition-transform" : "",
          ].join(" ")}
          onClick={() => interactive && onChange?.(s)}
          onMouseEnter={() => interactive && setHover(s)}
          onMouseLeave={() => interactive && setHover(0)}
        />
      ))}
    </div>
  );
};

/* ─── Ana bileşen ─── */
const ProductReviews: React.FC<{
  urunId: number;
  onOzetLoaded?: (ozet: YorumOzet) => void;
}> = ({ urunId, onOzetLoaded }) => {
  const { customer, token } = useCustomerAuth();

  const [ozet, setOzet] = useState<YorumOzet | null>(null);
  const [yorumlar, setYorumlar] = useState<Yorum[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [toplamSayfa, setToplamSayfa] = useState(1);

  const [musteriDurum, setMusteriDurum] = useState<MusteriDurum | null>(null);

  // Form state
  const [formPuan, setFormPuan] = useState(0);
  const [formMetin, setFormMetin] = useState("");
  const [formBusy, setFormBusy] = useState(false);
  const [formMsg, setFormMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  /* Yorumları yükle */
  const loadReviews = useCallback(async (p = 1) => {
    try {
      setLoading(true);
      const res = await axiosInstance.post("/UrunYorumlari/listele", {
        urunId, page: p, pageSize: 8,
      });
      if (res.data?.success) {
        const d = res.data.data;
        setOzet(d.ozet);
        setYorumlar(d.yorumlar ?? []);
        setToplamSayfa(d.toplamSayfa ?? 1);
        setPage(p);
        if (p === 1 && d.ozet && onOzetLoaded) onOzetLoaded(d.ozet);
      }
    } catch { /* yoksay */ }
    finally { setLoading(false); }
  }, [urunId]);

  /* Müşteri yorum durumu */
  const loadMusteriDurum = useCallback(async () => {
    if (!customer?.id || !token) return;
    try {
      const res = await axiosInstance.post("/UrunYorumlari/musteri-durumu", {
        urunId, musteriId: customer.id,
      });
      if (res.data?.success) setMusteriDurum(res.data.data);
    } catch { /* yoksay */ }
  }, [urunId, customer?.id, token]);

  useEffect(() => {
    loadReviews(1);
    loadMusteriDurum();
  }, [loadReviews, loadMusteriDurum]);

  /* Yorum gönder */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer?.id || !token) return;
    if (formPuan === 0) { setFormMsg({ type: "err", text: "Lütfen bir puan seçin." }); return; }
    if (formMetin.trim().length < 5) { setFormMsg({ type: "err", text: "Yorum en az 5 karakter olmalı." }); return; }

    setFormBusy(true);
    setFormMsg(null);
    try {
      const ad = [customer?.ad, customer?.soyad].filter(Boolean).join(" ").trim()
        || customer?.name || "";
      const res = await axiosInstance.post("/UrunYorumlari/ekle", {
        urunId,
        musteriId: customer.id,
        musteriAdSoyad: ad,
        puan: formPuan,
        yorumMetni: formMetin.trim(),
      });
      if (res.data?.success) {
        setFormMsg({ type: "ok", text: res.data.data ?? "Yorumunuz alındı, inceleme sürecindedir." });
        setFormPuan(0);
        setFormMetin("");
        loadMusteriDurum();
      } else {
        setFormMsg({ type: "err", text: res.data?.errorDetail || "Bir hata oluştu." });
      }
    } catch {
      setFormMsg({ type: "err", text: "Bağlantı hatası." });
    } finally {
      setFormBusy(false);
    }
  };

  /* ── Özet bar ── */
  const OzetBar: React.FC<{ label: string; count: number; total: number }> = ({ label, count, total }) => {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="w-4 text-slate-500 shrink-0">{label}</span>
        <Star size={11} className="fill-amber-400 text-amber-400 shrink-0" />
        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <span className="w-6 text-right text-slate-400 shrink-0">{count}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* ── Özet ── */}
      {ozet && (
        <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "rgba(15,23,42,.1)" }}>
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Ortalama */}
            <div className="flex flex-col items-center justify-center sm:w-40 shrink-0 gap-1">
              <div className="text-5xl font-bold text-slate-800">
                {ozet.ortalama.toFixed(1)}
              </div>
              <Stars value={Math.round(ozet.ortalama)} size={20} />
              <div className="text-xs text-slate-400 mt-1">
                {ozet.toplamAdet} değerlendirme
              </div>
            </div>

            {/* Dağılım */}
            <div className="flex-1 space-y-1.5">
              <OzetBar label="5" count={ozet.puan5} total={ozet.toplamAdet} />
              <OzetBar label="4" count={ozet.puan4} total={ozet.toplamAdet} />
              <OzetBar label="3" count={ozet.puan3} total={ozet.toplamAdet} />
              <OzetBar label="2" count={ozet.puan2} total={ozet.toplamAdet} />
              <OzetBar label="1" count={ozet.puan1} total={ozet.toplamAdet} />
            </div>
          </div>
        </div>
      )}

      {/* ── Yorum formu ── */}
      {!token ? (
        <div className="rounded-2xl border bg-white p-5 text-center" style={{ borderColor: "rgba(15,23,42,.1)" }}>
          <div className="text-sm text-slate-500 mb-3">Yorum yapmak için giriş yapmalısınız.</div>
          <Link
            to="/musteri/giris"
            className="h-9 px-5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-teal-500 inline-flex items-center"
          >
            Giriş Yap
          </Link>
        </div>
      ) : musteriDurum?.yorumYapildi ? (
        /* Daha önce yorum yapmış */
        <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "rgba(15,23,42,.1)" }}>
          <div className="flex items-start gap-3">
            {musteriDurum.onayliMi
              ? <CheckCircle size={18} className="text-green-500 mt-0.5 shrink-0" />
              : <Clock size={18} className="text-amber-500 mt-0.5 shrink-0" />
            }
            <div>
              <div className="text-sm font-semibold text-slate-800 mb-1">
                {musteriDurum.onayliMi ? "Yorumunuz yayında" : "Yorumunuz incelemede"}
              </div>
              <Stars value={musteriDurum.puan} size={14} />
              {musteriDurum.yorumMetni && (
                <p className="mt-1.5 text-sm text-slate-600">{musteriDurum.yorumMetni}</p>
              )}
            </div>
          </div>
        </div>
      ) : musteriDurum !== null && !musteriDurum.satinAldi ? (
        /* Satın almamış — form yerine bilgi mesajı */
        <div className="rounded-2xl border bg-white p-5 flex items-start gap-3" style={{ borderColor: "rgba(15,23,42,.1)" }}>
          <div className="w-10 h-10 rounded-full bg-amber-50 grid place-items-center shrink-0">
            <Star size={18} className="text-amber-400" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-800 mb-1">Yorum yapabilmek için ürünü satın almalısınız</div>
            <div className="text-xs text-slate-500">
              Bu ürünü sipariş verip teslim aldıktan sonra yorum yapabilirsiniz.
            </div>
            <Link
              to="/products"
              className="mt-3 h-9 px-4 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-teal-500 hover:opacity-90 transition inline-flex items-center gap-1.5"
            >
              Ürünlere Git →
            </Link>
          </div>
        </div>
      ) : musteriDurum?.satinAldi ? (
        /* Satın almış, henüz yorum yazmamış */
        <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "rgba(15,23,42,.1)" }}>
          <h4 className="text-sm font-bold text-slate-800 mb-4">Yorum Yaz</h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Puan */}
            <div>
              <div className="text-xs text-slate-500 mb-1.5">Puanınız</div>
              <Stars value={formPuan} size={28} interactive onChange={setFormPuan} />
            </div>

            {/* Metin */}
            <div>
              <div className="text-xs text-slate-500 mb-1.5">Yorumunuz</div>
              <textarea
                value={formMetin}
                onChange={(e) => setFormMetin(e.target.value)}
                placeholder="Bu ürün hakkında deneyiminizi paylaşın..."
                rows={3}
                maxLength={2000}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 resize-none"
              />
              <div className="text-right text-xs text-slate-300 mt-0.5">{formMetin.length}/2000</div>
            </div>

            {formMsg && (
              <div className={[
                "text-xs px-3 py-2 rounded-xl",
                formMsg.type === "ok"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-700 border border-red-200",
              ].join(" ")}>
                {formMsg.text}
              </div>
            )}

            <button
              type="submit"
              disabled={formBusy}
              className="h-10 px-5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-teal-500 hover:opacity-90 transition inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Send size={14} />
              {formBusy ? "Gönderiliyor..." : "Yorumu Gönder"}
            </button>
          </form>
        </div>
      ) : null}

      {/* ── Yorum listesi ── */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-2xl bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : yorumlar.length === 0 ? (
        <div className="rounded-2xl border bg-white p-6 text-center" style={{ borderColor: "rgba(15,23,42,.1)" }}>
          <Star size={32} className="mx-auto text-slate-200 mb-2" />
          <div className="text-sm text-slate-400">Henüz yorum yok. İlk yorumu siz yapın!</div>
        </div>
      ) : (
        <div className="space-y-3">
          {yorumlar.map((y) => (
            <div
              key={y.yorumId}
              className="rounded-2xl border bg-white p-4"
              style={{ borderColor: "rgba(15,23,42,.08)" }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-teal-100 grid place-items-center text-xs font-bold text-blue-700">
                    {y.musteriAdSoyad?.[0]?.toUpperCase() ?? "M"}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{y.musteriAdSoyad}</div>
                    <Stars value={y.puan} size={12} />
                  </div>
                </div>
                <div className="text-xs text-slate-400 shrink-0">
                  {new Date(y.olusturmaTarihi).toLocaleDateString("tr-TR")}
                </div>
              </div>
              {y.yorumMetni && (
                <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{y.yorumMetni}</p>
              )}
            </div>
          ))}

          {/* Sayfalama */}
          {toplamSayfa > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                onClick={() => loadReviews(page - 1)}
                disabled={page <= 1}
                className="h-9 w-9 rounded-xl border bg-white inline-flex items-center justify-center hover:bg-slate-50 disabled:opacity-30"
                style={{ borderColor: "rgba(15,23,42,.1)" }}
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-slate-500">
                {page} / {toplamSayfa}
              </span>
              <button
                onClick={() => loadReviews(page + 1)}
                disabled={page >= toplamSayfa}
                className="h-9 w-9 rounded-xl border bg-white inline-flex items-center justify-center hover:bg-slate-50 disabled:opacity-30"
                style={{ borderColor: "rgba(15,23,42,.1)" }}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { Stars };
export default ProductReviews;
